/* eslint-disable @typescript-eslint/no-non-null-assertion */

import {FC, RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {yupResolver} from '@hookform/resolvers/yup'
import {addBreadcrumb, Severity} from '@sentry/nextjs'
import cardValidator from 'card-validator'
import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import Link from 'next/link'
import {useRouter} from 'next/router'
import Script from 'next/script'
import {FormProvider, useForm} from 'react-hook-form'
import {v4 as uuid} from 'uuid'
import * as yup from 'yup'

import Button from 'components/elements/Button'
import FieldCheckbox from 'components/elements/fields/FieldCheckbox'
import FieldInput from 'components/elements/fields/FieldInput'
import MessageBlock from 'components/elements/MessageBlock'
import {ITds, ITdsCredentials} from 'src/types/threeds'
import {posthogTrack, klaviyoTrack, pushGTMLayer} from 'src/utils'
import {useAuthStore, useDraftStore, usePaymentStore} from 'src/utils/contexts/RootStore.context'
import useAsyncFlow from 'src/utils/hooks/useAsyncFlow'
import {useToggleState} from 'src/utils/hooks/useToggleState'
import {messages} from 'src/utils/validations'

import s from './CheckoutForm.module.scss'
import GooglePayButton from './GooglePayButton'
import BillingsTermsModal from '../BillingsTermsModal'

const cx = cn.bind(s)
const THREE_DS_AMOUNT = 1

const getIsAmericanExpress = (v: string) =>
	cardValidator.number(v).card?.type === 'american-express'
const getIsHipercard = (v: string) => cardValidator.number(v).card?.type === 'hipercard'

export const paymentFormSchema = yup
	.object({
		cardNumber: yup
			.string()
			.required(messages.required)
			.test(
				'credit-card-number',
				'credit Card number is invalid',
				(value) => cardValidator.number(value).isValid,
			),
		expiration: yup
			.string()
			.required(messages.required)
			.test(
				'expiration-date',
				'expiration date is invalid',
				(value) => cardValidator.expirationDate(value).isValid,
			),
		cvc: yup
			.string()
			.required(messages.required)
			.test('cvc', 'CVC or CID is invalid', (value, ctx) => {
				const withFourSymbols =
					getIsAmericanExpress(ctx.parent.creditCard) || getIsHipercard(ctx.parent.creditCard)

				if (withFourSymbols) return cardValidator.cvv(value, 4).isValid
				return cardValidator.cvv(value).isValid
			}),
		zip: yup
			.string()
			.required(messages.required)
			.test(
				'postal-code',
				'Zip code is invalid',
				(value) => cardValidator.postalCode(value).isValid,
			),
		agreement: yup
			.boolean()
			.required(messages.required)
			.oneOf([true], 'The terms and conditions must be accepted.'),
	})
	.required()

interface ICheckoutFormProps {
	planConfig: IPaymentPlanConfig
}

const CheckoutForm: FC<ICheckoutFormProps> = ({planConfig}) => {
	const authStore = useAuthStore()
	const draftStore = useDraftStore()
	const paymentStore = usePaymentStore()
	const router = useRouter()
	const flexcharge_order_id = useMemo(() => uuid(), [])

	const planPayButtonText = planConfig.subscription.frequency !== 'once' ? 'Subscribe' : 'Purchase'

	const [isOpen, togglePopup] = useToggleState(false)
	const formRef: RefObject<HTMLFormElement> = useRef(null)
	const [tds, setTds] = useState<ITds | null>(null)
	const [tdsCredentials, setTdsCredentials] = useState<ITdsCredentials | null>(null)
	const [isRedirecting, setIsRedirecting] = useState(false)
	const [paymentsFlow, {isLoading, errorMessage}] = useAsyncFlow<typeof paymentStore.makePayment>(
		paymentStore.makePayment,
	)
	const [fetchProfileDataFlow] = useAsyncFlow<typeof authStore.fetchProfileData>(
		authStore.fetchProfileData,
	)

	const methods = useForm<yup.TypeOf<typeof paymentFormSchema>>({
		resolver: yupResolver(paymentFormSchema),
		mode: 'onChange',
		defaultValues: {
			cardNumber: '',
			expiration: '',
			cvc: '',
			zip: '',
			agreement: false,
		},
	})

	const {cardNumber, expiration} = methods.getValues()

	useEffect(() => {
		const subscription = methods.watch((fields) => {
			paymentStore.data = fields as yup.TypeOf<typeof paymentFormSchema>
		})
		return subscription.unsubscribe
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const threeDSVerify = () => {
		if (!tdsCredentials && tds) {
			tds.verify(
				(res: ITdsCredentials) => {
					setTdsCredentials(res)
				},
				null,
				{amount: THREE_DS_AMOUNT},
			)
		}
	}
	const onInputBlur = () => {
		threeDSVerify()
	}

	const onSuccess = useCallback(async () => {
		setIsRedirecting(true)

		pushGTMLayer({
			event: 'purchased',
			paymentType: planConfig.key,
		})

		// NOTE: before it was 'purchase' event
		posthogTrack('$revenue', {
			paymentType: planConfig.key,
			price: planConfig.subscription.price,
		})

		klaviyoTrack('purchase', {
			paymentType: planConfig.key,
		})
		// update subscription info in the Auth store
		await fetchProfileDataFlow()()

		// it's a part of the checkout flow so after payment the user continues working with the document if it exists
		const redirectPath = draftStore.draftId ? '/download' : '/'
		router.push(redirectPath)
	}, [draftStore.draftId, planConfig, router, fetchProfileDataFlow])

	const onSubmit = useCallback(
		async (data: yup.TypeOf<typeof paymentFormSchema>) => {
			posthogTrack('[Payment] Pay button clicked')
			addBreadcrumb({
				message: 'User store',
				level: Severity.Log,
				data: {
					store: authStore.user,
				},
			})

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const w = window as any
			if (w && w.fcInited === true) {
				console.log('FLEX CHARGE ORDER ID 🚀', flexcharge_order_id)
				w.fc.setOrderId({orderId: flexcharge_order_id})
				console.log(
					'FLEX CHARGE setOrderId was called 🚀🚀🚀 with the order id:',
					flexcharge_order_id,
				)
			} else {
				console.log('FLEX CHARGE is not inited 🤔')
			}

			await paymentsFlow({
				formData: {
					cardNumber: data.cardNumber!,
					expiration: data.expiration!,
					cvc: data.cvc!,
					agreement: data.agreement!,
					cavv: tdsCredentials?.cavv || '',
					eci: tdsCredentials?.eci || '',
					xid: tdsCredentials?.xid || '',
					zip: data.zip || '',
					flexcharge_order_id,
				},
				paymentType: planConfig.key,
				userId: authStore.user?.id || '',
			})({
				onSuccess,
				onError: () => {
					posthogTrack('[Error] Error in async operation', {
						info: 'Payment was failed',
						paymentType: planConfig.key,
						userId: authStore.user?.id,
					})
				},
			})
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			authStore.user?.id,
			authStore,
			draftStore.draftId,
			paymentsFlow,
			planConfig,
			router,
			// eslint-disable-next-line react-hooks/exhaustive-deps
			(window as any).fc,
			flexcharge_order_id,
		],
	)

	return (
		<FormProvider {...methods}>
			<form ref={formRef} className={cx('form')} data-testid="checkout-form" id="checkout-form">
				<ThreeDSVerification
					card={cardNumber?.replaceAll(' ', '')}
					month={expiration?.slice(0, 2)}
					year={expiration?.slice(3, 5)}
					setTds={setTds}
				/>
				<div className={cx('formInfo')}>
					<div className={cx('cardsImageContainer')}>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img className={cx('paymentImage')} src="/images/checkout/cards.png" alt={''} />
					</div>
					<p className={cx('totalWrapper')}>
						<span className={cx('totalLabel')}>Total:</span>
						<span className={cx('totalValue')}>
							${planConfig.trial ? planConfig.trial.price : planConfig.subscription.price}
						</span>
					</p>
				</div>
				<fieldset className={cx('fieldset')}>
					<FieldInput
						option="card"
						name="cardNumber"
						placeholder="1234 1234 1234 1234"
						variant="brainable"
						label="Card number"
						data-testid="card-number"
					/>
				</fieldset>
				<fieldset className={cx('fieldset')}>
					<FieldInput
						option="expiration-date"
						name="expiration"
						data-testid="card-expiration-date"
						placeholder="MM / YY"
						label="Expiration date"
						variant="brainable"
						onBlur={onInputBlur}
					/>
				</fieldset>
				<fieldset className={cx('fieldset')}>
					<FieldInput
						name="cvc"
						data-testid="card-cvc"
						label="CVC"
						placeholder="CVC"
						variant="brainable"
					/>
				</fieldset>
				<fieldset className={cx('fieldset')}>
					<FieldInput
						name="zip"
						data-testid="postal-code"
						label="Postal code"
						variant="brainable"
						placeholder="90210"
						type="tel"
						inputMode="numeric"
					/>
				</fieldset>
				<FieldCheckbox
					className={cx('terms')}
					name="agreement"
					data-testid="agreement"
					showError={false}
					showRequired={false}
					required
					label={
						<span className={cx('agreementText')}>
							By clicking &quot;{planPayButtonText}&quot;, you agree to provide your electronic
							signature authorizing Paystub.org to charge your card as described
							{planConfig.subscription.frequency !== 'once' ? (
								<>
									{' '}
									in the{' '}
									<button
										className={cx('link', 'billing-terms')}
										type="button"
										onClick={togglePopup}>
										Billing Terms
									</button>
									, including for the automatic renewal of your membership, until you cancel
								</>
							) : (
								''
							)}
							. I agree to the Paystub.org{' '}
							<Link href="/terms" target="_blank" className={cx('link')}>
								Terms of Use
							</Link>{' '}
							and{' '}
							<Link href="/privacy-policy" target="_blank" className={cx('link')}>
								Privacy Policy
							</Link>
							.
						</span>
					}
				/>
				{errorMessage && (
					<MessageBlock type="error" className={cx('error')} message={errorMessage} />
				)}
				{methods.formState.errors && (
					<MessageBlock type="error" className={cx('error')} message={methods.formState.errors} />
				)}
				{authStore.hasActiveSubscription && (
					<MessageBlock
						type="error"
						className={cx('error')}
						message="You already have a subscription."
					/>
				)}
				<Button
					className={cx('btn')}
					type="submit"
					data-testid="pay"
					variant="brainableStatic"
					isLoading={isLoading || isRedirecting}
					disabled={authStore.hasActiveSubscription}
					onClick={methods.handleSubmit(onSubmit)}>
					{planPayButtonText}
				</Button>
				{window &&
					window.location &&
					window.location.search &&
					window.location.search.includes('googlepay') && <GooglePayButton />}
			</form>
			<BillingTermsPopup planConfig={planConfig} isOpen={isOpen} onClose={togglePopup} />
		</FormProvider>
	)
}

interface IBillingTermsPopupProps {
	planConfig: IPaymentPlanConfig
	isOpen: boolean
	onClose: () => void
}

const BillingTermsPopup: FC<IBillingTermsPopupProps> = ({planConfig, isOpen, onClose}) => {
	const billingTermsText = useMemo(() => {
		let text = ''
		if (planConfig.trial) {
			text += `Your $${planConfig.trial.price} package lasts ${planConfig.trial.duration} days, and`
		} else {
			text += `Your package $${planConfig.subscription.price}`
		}
		text += ` will renew ${planConfig.subscription.frequency}
		  unless you cancel before the start of the next term. Paystub.org will
		  charge the recurring membership fee of $${planConfig.subscription.price} 
		  to the same payment option you use today until you cancel, which can be done
		  at by contacting customer support at 855-607-1274 / support@paystub.org
		`
		return text
	}, [planConfig])
	useEffect(() => {
		if (isOpen) {
			posthogTrack('Billing details popup opened', {source: 'Checkout form'})
		}
	}, [isOpen])

	return (
		<BillingsTermsModal
			isOpen={isOpen}
			header="Billing Details"
			className={cx('billingPopupContent')}
			onClose={onClose}>
			{billingTermsText}
		</BillingsTermsModal>
	)
}

interface IThreeDSVerificationProps {
	setTds: (tds: ITds) => void
	card?: string
	month?: string
	year?: string
}

const ThreeDSVerification: FC<IThreeDSVerificationProps> = ({setTds, month, year, card}) => {
	const [isLoaded3Ds, setIsLoaded3Ds] = useState(false)

	useEffect(() => {
		try {
			if (isLoaded3Ds) {
				const options = {
					verbose: process.env.NODE_ENV !== 'production', // For testing purposes (Set to false in production mode)
					autoSubmit: false,
					addResultToForm: true,
					allowRetry: true,
					showIframe: false,
					iframeId: 'tds-iframe',
				}

				const tds = new window.ThreeDS(
					'checkout-form',
					process.env.NEXT_PUBLIC_THREE_DS_API_KEY!,
					null,
					options,
				)
				setTds(tds)
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.warn(error)
		}
	}, [isLoaded3Ds, setTds])

	const on3DSLoaded = useCallback(() => {
		setIsLoaded3Ds(true)
	}, [])
	return (
		<div>
			<Script
				src="https://cdn.3dsintegrator.com/threeds.2.2.20230227.min.js"
				onLoad={on3DSLoaded}
			/>
			<input type="hidden" name="x_card_num" data-threeds="pan" value={card || ''} />
			<input type="hidden" name="x_exp_month" data-threeds="month" value={month || ''} />
			<input type="hidden" name="x_exp_year" data-threeds="year" value={year || ''} />
		</div>
	)
}

export default observer(CheckoutForm)
